import { ethers } from 'ethers';
import { PlanType } from '../types';

interface PaymentRecord {
  txHash: string;
  payer: string;
  amount: string;
  token: string;
  plan: PlanType;
  duration: number; // months
  timestamp: number;
  expiresAt: number;
  verified: boolean;
}

interface PlanPricing {
  monthly: string;
  yearly: string; // 20% discount
  token: 'ETH' | 'USDC';
}

export class PaymentVerificationService {
  private provider: ethers.Provider;
  private paymentRecords: Map<string, PaymentRecord[]> = new Map();
  private apiKeyToAddress: Map<string, string> = new Map();
  private addressToPlan: Map<string, { plan: PlanType; expiresAt: number }> = new Map();
  
  // Pricing in USD (converted to ETH/USDC at payment time)
  private static PLAN_PRICING: Record<PlanType, PlanPricing> = {
    [PlanType.FREE]: {
      monthly: '0',
      yearly: '0',
      token: 'ETH'
    },
    [PlanType.STARTER]: {
      monthly: '29', // $29/month
      yearly: '278.4', // $29 * 12 * 0.8 (20% discount)
      token: 'USDC'
    },
    [PlanType.PRO]: {
      monthly: '99', // $99/month
      yearly: '950.4', // $99 * 12 * 0.8
      token: 'USDC'
    },
    [PlanType.ENTERPRISE]: {
      monthly: '499', // $499/month (custom pricing available)
      yearly: '4790.4', // $499 * 12 * 0.8
      token: 'USDC'
    }
  };

  // Payment address for receiving payments
  private static PAYMENT_ADDRESS = process.env.LEGACYCHAIN_PAYMENT_ADDRESS || '0x742d35Cc6634C0532925a3b844Bc9e7595f6E3D2';

  constructor(provider?: ethers.Provider) {
    this.provider = provider || new ethers.JsonRpcProvider(process.env.RPC_URL || 'https://rpc.ankr.com/eth');
    
    // Initialize with some mock data for development
    if (process.env.NODE_ENV === 'development') {
      this.initializeMockData();
    }
  }

  /**
   * Verify a payment transaction and activate the plan
   */
  async verifyPayment(txHash: string, expectedPlan: PlanType, duration: number = 1): Promise<boolean> {
    try {
      const tx = await this.provider.getTransaction(txHash);
      if (!tx) {
        throw new Error('Transaction not found');
      }

      const receipt = await this.provider.getTransactionReceipt(txHash);
      if (!receipt || receipt.status !== 1) {
        throw new Error('Transaction failed or not confirmed');
      }

      // Verify payment recipient
      if (tx.to?.toLowerCase() !== PaymentVerificationService.PAYMENT_ADDRESS.toLowerCase()) {
        throw new Error('Invalid payment address');
      }

      // Verify amount matches plan pricing
      const pricing = PaymentVerificationService.PLAN_PRICING[expectedPlan];
      const expectedAmount = duration === 12 ? pricing.yearly : (parseFloat(pricing.monthly) * duration).toString();
      
      // For ETH payments, check value
      if (pricing.token === 'ETH') {
        const ethAmount = ethers.formatEther(tx.value);
        // Add some tolerance for ETH price fluctuations (5%)
        const tolerance = parseFloat(expectedAmount) * 0.05;
        if (Math.abs(parseFloat(ethAmount) - parseFloat(expectedAmount)) > tolerance) {
          throw new Error('Incorrect payment amount');
        }
      } else {
        // For token payments, decode the transfer event
        // This would need the actual token contract ABI
        // For now, we'll trust the amount
      }

      // Create payment record
      const paymentRecord: PaymentRecord = {
        txHash,
        payer: tx.from,
        amount: expectedAmount,
        token: pricing.token,
        plan: expectedPlan,
        duration,
        timestamp: Date.now(),
        expiresAt: Date.now() + (duration * 30 * 24 * 60 * 60 * 1000), // duration in months
        verified: true
      };

      // Store payment record
      const records = this.paymentRecords.get(tx.from) || [];
      records.push(paymentRecord);
      this.paymentRecords.set(tx.from, records);

      // Update plan
      this.addressToPlan.set(tx.from, {
        plan: expectedPlan,
        expiresAt: paymentRecord.expiresAt
      });

      return true;
    } catch (error) {
      console.error('Payment verification failed:', error);
      return false;
    }
  }

  /**
   * Get the current plan for an API key
   */
  getPlanForApiKey(apiKey: string): PlanType {
    const address = this.apiKeyToAddress.get(apiKey);
    if (!address) {
      return PlanType.FREE;
    }

    const planInfo = this.addressToPlan.get(address);
    if (!planInfo) {
      return PlanType.FREE;
    }

    // Check if plan is expired
    if (Date.now() > planInfo.expiresAt) {
      return PlanType.FREE;
    }

    return planInfo.plan;
  }

  /**
   * Link an API key to a wallet address
   */
  linkApiKeyToAddress(apiKey: string, address: string): void {
    this.apiKeyToAddress.set(apiKey, address.toLowerCase());
  }

  /**
   * Get payment history for an address
   */
  getPaymentHistory(address: string): PaymentRecord[] {
    return this.paymentRecords.get(address.toLowerCase()) || [];
  }

  /**
   * Get pricing information
   */
  static getPricing(): Record<PlanType, PlanPricing> {
    return this.PLAN_PRICING;
  }

  /**
   * Generate a payment request
   */
  generatePaymentRequest(plan: PlanType, duration: number = 1): {
    to: string;
    amount: string;
    token: string;
    data: string;
  } {
    const pricing = PaymentVerificationService.PLAN_PRICING[plan];
    const amount = duration === 12 ? pricing.yearly : (parseFloat(pricing.monthly) * duration).toString();

    return {
      to: PaymentVerificationService.PAYMENT_ADDRESS,
      amount,
      token: pricing.token,
      data: ethers.id(`LegacyChain:${plan}:${duration}months`).slice(0, 10) // First 4 bytes as identifier
    };
  }

  /**
   * Check if a plan is active
   */
  isPlanActive(address: string): boolean {
    const planInfo = this.addressToPlan.get(address.toLowerCase());
    return planInfo ? Date.now() < planInfo.expiresAt : false;
  }

  /**
   * Get days until plan expires
   */
  getDaysUntilExpiry(address: string): number {
    const planInfo = this.addressToPlan.get(address.toLowerCase());
    if (!planInfo) return 0;

    const daysRemaining = Math.max(0, planInfo.expiresAt - Date.now()) / (24 * 60 * 60 * 1000);
    return Math.floor(daysRemaining);
  }

  private initializeMockData(): void {
    // Add some test data for development
    const testAddress = '0x1234567890123456789012345678901234567890';
    const testApiKey = 'test-api-key-pro';

    this.linkApiKeyToAddress(testApiKey, testAddress);
    this.addressToPlan.set(testAddress, {
      plan: PlanType.PRO,
      expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000 // 30 days
    });

    console.log('Initialized mock payment data for development');
  }
} 